import type { PointerEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const ROOT_FONT_SIZE_FALLBACK = 16;
const COLLAPSED_SNAP_POINT_REM = 5;
const HALF_OPEN_SNAP_POINT_REM = 8.5;
const COLLAPSED_SNAP_POINT = `${COLLAPSED_SNAP_POINT_REM}rem`;
const HALF_OPEN_SNAP_POINT = `${HALF_OPEN_SNAP_POINT_REM}rem`;
const FULL_OPEN_SNAP_POINT = 'calc(100dvh - 4rem)';

// Numeric heights used for drag calculations. Full open is calculated from the viewport,
// because the CSS snap point uses calc() while drag math needs a concrete px value.
const SNAP_POINT_HEIGHTS_REM = [COLLAPSED_SNAP_POINT_REM, HALF_OPEN_SNAP_POINT_REM, null] as const;
// Minimum swipe distance required to move the bar to the next snap point.
const NEXT_SNAP_DRAG_DISTANCE = 28;
// Larger upward swipe from half-open opens the full Game Center immediately.
const FULL_OPEN_DRAG_DISTANCE = 86;
// Larger downward swipe from full-open collapses the bar immediately.
const FULL_CLOSE_DRAG_DISTANCE = 96;
// Downward drag visually opens the bar faster so the gesture feels more responsive.
const DRAG_OPEN_GAIN = 1.25;
export const GM_BAR_MOBILE_EXPANDED_EVENT = 'gm-bar-mobile:expanded';

// UI snap points: collapsed, half-open, and full-open.
export const SNAP_POINTS = [COLLAPSED_SNAP_POINT, HALF_OPEN_SNAP_POINT, FULL_OPEN_SNAP_POINT] as const;

const getRootFontSize = () => {
  if (typeof window === 'undefined') {
    return ROOT_FONT_SIZE_FALLBACK;
  }

  const fontSize = Number.parseFloat(window.getComputedStyle(document.documentElement).fontSize);

  return Number.isFinite(fontSize) ? fontSize : ROOT_FONT_SIZE_FALLBACK;
};

const remToPx = (value: number) => value * getRootFontSize();

export const useGmBarMobileControls = () => {
  // Current snap point index. Drag height temporarily overrides it only while swiping.
  const [activeSnapPointIndex, setActiveSnapPointIndex] = useState(0);
  const [dragHeight, setDragHeight] = useState<number | null>(null);
  // Swipe start state: finger coordinate and bar height at pointer down.
  const dragStartRef = useRef<{ y: number; height: number } | null>(null);
  // RAF prevents setting state on pointer move more often than the browser paints a frame.
  const dragAnimationFrameRef = useRef<number | null>(null);
  // Latest height is stored in a ref because pointer up can fire before the RAF state update is applied.
  const latestDragHeightRef = useRef<number | null>(null);

  const isFullyOpen = activeSnapPointIndex === SNAP_POINTS.length - 1;
  const activeHeight = useMemo(() => {
    // While dragging, use the live px height. After drag ends, return to the selected snap point.
    if (dragHeight !== null) {
      return `${dragHeight}px`;
    }

    return SNAP_POINTS[activeSnapPointIndex] ?? COLLAPSED_SNAP_POINT;
  }, [activeSnapPointIndex, dragHeight]);

  const getCurrentHeight = useCallback(() => {
    const activeSnapPointHeightRem = SNAP_POINT_HEIGHTS_REM[activeSnapPointIndex];

    if (activeSnapPointHeightRem !== null && activeSnapPointHeightRem !== undefined) {
      return remToPx(activeSnapPointHeightRem);
    }

    // For full-open, use viewport height minus top offset so drag math matches the CSS calc().
    if (typeof window === 'undefined') {
      return remToPx(HALF_OPEN_SNAP_POINT_REM);
    }

    return window.innerHeight - remToPx(COLLAPSED_SNAP_POINT_REM);
  }, [activeSnapPointIndex]);

  const visibleHeight = dragHeight ?? getCurrentHeight();
  const collapsedSnapPointHeight = remToPx(COLLAPSED_SNAP_POINT_REM);
  const halfOpenSnapPointHeight = remToPx(HALF_OPEN_SNAP_POINT_REM);
  // Reveal progress for the status row between collapsed and half-open states.
  const statusRevealProgress = Math.max(
    0,
    Math.min(1, (visibleHeight - collapsedSnapPointHeight) / (halfOpenSnapPointHeight - collapsedSnapPointHeight)),
  );
  // Mount the status row slightly before it is fully visible so its animation is not clipped.
  const shouldRenderStatusRow = activeSnapPointIndex > 0 || statusRevealProgress > 0.05;

  const getTargetSnapPointIndex = useCallback((height: number) => {
    if (!dragStartRef.current) {
      return activeSnapPointIndex;
    }

    const dragDistance = height - dragStartRef.current.height;

    // Long swipe from half-open/collapsed takes priority over step-by-step movement.
    if (dragDistance > FULL_OPEN_DRAG_DISTANCE && activeSnapPointIndex >= 1) {
      return 2;
    }

    // From full-open, allow a hard close so the user does not need two separate swipes.
    if (dragDistance < -FULL_CLOSE_DRAG_DISTANCE && activeSnapPointIndex === 2) {
      return 0;
    }

    // Short swipes move the bar by only one neighboring snap point.
    if (dragDistance > NEXT_SNAP_DRAG_DISTANCE) {
      return Math.min(activeSnapPointIndex + 1, SNAP_POINTS.length - 1);
    }

    if (dragDistance < -NEXT_SNAP_DRAG_DISTANCE) {
      return Math.max(activeSnapPointIndex - 1, 0);
    }

    return activeSnapPointIndex;
  }, [activeSnapPointIndex]);

  const updateDragHeight = useCallback((height: number) => {
    // Update the ref synchronously and state in RAF so the final snap keeps the latest finger movement.
    latestDragHeightRef.current = height;

    if (dragAnimationFrameRef.current !== null) {
      window.cancelAnimationFrame(dragAnimationFrameRef.current);
    }

    dragAnimationFrameRef.current = window.requestAnimationFrame(() => {
      setDragHeight(height);
      dragAnimationFrameRef.current = null;
    });
  }, []);

  useEffect(() => {
    // On unmount, clear the scheduled frame to avoid setting state after the component is removed.
    return () => {
      if (dragAnimationFrameRef.current !== null) {
        window.cancelAnimationFrame(dragAnimationFrameRef.current);
      }
    };
  }, []);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent(GM_BAR_MOBILE_EXPANDED_EVENT, { detail: isFullyOpen }));

    return () => {
      window.dispatchEvent(new CustomEvent(GM_BAR_MOBILE_EXPANDED_EVENT, { detail: false }));
    };
  }, [isFullyOpen]);

  useEffect(() => {
    if (!isFullyOpen) {
      return;
    }

    const originalOverflow = document.body.style.overflow;
    // Full-open bar has its own scroll area, so page scroll underneath is locked.
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isFullyOpen]);

  const handlePointerDown = useCallback((event: PointerEvent<HTMLDivElement>) => {
    // Pointer capture keeps dragging active even if the finger leaves the handle bounds.
    event.currentTarget.setPointerCapture(event.pointerId);
    dragStartRef.current = {
      y: event.clientY,
      height: getCurrentHeight(),
    };
  }, [getCurrentHeight]);

  const handlePointerMove = useCallback((event: PointerEvent<HTMLDivElement>) => {
    if (!dragStartRef.current || typeof window === 'undefined') {
      return;
    }

    const dragDistance = event.clientY - dragStartRef.current.y;
    const gainedDragDistance = dragDistance > 0 ? dragDistance * DRAG_OPEN_GAIN : dragDistance;
    const collapsedSnapPointHeight = remToPx(COLLAPSED_SNAP_POINT_REM);
    const fullOpenSnapPointHeight = window.innerHeight - collapsedSnapPointHeight;
    // Clamp live height between collapsed and full-open so the bar stays inside the viewport.
    const nextHeight = Math.min(
      fullOpenSnapPointHeight,
      Math.max(collapsedSnapPointHeight, dragStartRef.current.height + gainedDragDistance),
    );

    updateDragHeight(nextHeight);
  }, [updateDragHeight]);

  const handlePointerUp = useCallback(() => {
    if (!dragStartRef.current) {
      return;
    }

    if (dragAnimationFrameRef.current !== null) {
      window.cancelAnimationFrame(dragAnimationFrameRef.current);
      dragAnimationFrameRef.current = null;
    }

    // After the gesture ends, snap the live height to the target snap point.
    setActiveSnapPointIndex(getTargetSnapPointIndex(latestDragHeightRef.current ?? dragHeight ?? dragStartRef.current.height));
    setDragHeight(null);
    dragStartRef.current = null;
    latestDragHeightRef.current = null;
  }, [dragHeight, getTargetSnapPointIndex]);

  const handleAwardsButtonClick = useCallback(() => {
    if (dragAnimationFrameRef.current !== null) {
      window.cancelAnimationFrame(dragAnimationFrameRef.current);
      dragAnimationFrameRef.current = null;
    }

    // Awards button opens the full Game Center directly and clears any active gesture state.
    setDragHeight(null);
    dragStartRef.current = null;
    latestDragHeightRef.current = null;
    setActiveSnapPointIndex(SNAP_POINTS.length - 1);
  }, []);

  return {
    activeHeight,
    activeSnapPointIndex,
    dragHeight,
    handleAwardsButtonClick,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    isFullyOpen,
    shouldRenderStatusRow,
    statusRevealProgress,
  };
};
